import {
  buildCollage,
  buildParentMap,
  COLLAGE_LAYOUTS,
  type CollageLayout,
  type CollageOptions,
  collageCells,
  collagePageBox,
  defaultCollageGap,
  getCollageLayout,
  insertElements,
  isImagePlaceholder,
  MASK_PRESETS,
  pageSize,
  relayoutCollage,
  removeElements,
  worldBox,
} from "@cva/shared";
import { useState } from "react";
import { cn } from "../lib/utils";
import { MaskIcon } from "./MaskPicker";
import { type EditorStore, useEditor, useEditorApi } from "./store";
import { Slider } from "./ui";

/** Forms offered for all cells (a short list; "Keine" = rectangle). */
const CELL_MASKS = ["circle", "arch", "heart", "blob1", "hexagon"]
  .map((id) => MASK_PRESETS.find((preset) => preset.id === id))
  .filter((preset) => preset !== undefined);

/** The collage inserted last (options change it while it stays selected). */
let lastCollage: { groupId: string; layoutId: string } | null = null;

function CollagePreview({ layout }: { layout: CollageLayout }) {
  const cells = collageCells(layout, { x: 2, y: 2, width: 40, height: 40 }, 2.5);
  return (
    <svg width={44} height={44} viewBox="0 0 44 44" aria-hidden="true">
      {cells.map((cell, index) => (
        <rect
          // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
          key={index}
          x={cell.x}
          y={cell.y}
          width={cell.width}
          height={cell.height}
          rx={1.5}
          fill="currentColor"
          fillOpacity={0.3}
        />
      ))}
    </svg>
  );
}

function options(gap: number, radius: number, maskId: string | null): CollageOptions {
  const mask = MASK_PRESETS.find((preset) => preset.id === maskId)?.mask;
  return { gap, radius, ...(mask ? { mask } : {}) };
}

/** Whether the collage inserted last is the (only) selection. */
function selectedCollage(store: EditorStore) {
  const { selection, doc } = store.getState();
  if (!lastCollage || selection.length !== 1 || selection[0] !== lastCollage.groupId) return null;
  return doc.elements[lastCollage.groupId]?.type === "group" ? lastCollage : null;
}

/**
 * Inserts a collage: into the box of the selected element (a selected image
 * placeholder is replaced), else onto the page inside its margins. Clicking
 * another layout while the last collage is selected rebuilds it in place;
 * with the same number of cells the pictures stay.
 */
function insertCollage(store: EditorStore, layout: CollageLayout, opts: CollageOptions) {
  const state = store.getState();
  const { doc, pageIndex, selection } = state;
  const current = selectedCollage(store);
  if (current) {
    const group = doc.elements[current.groupId];
    if (group?.type === "group" && group.children.length === layout.cells.length) {
      state.update((draft) => {
        relayoutCollage(draft, current.groupId, layout, opts);
        const target = draft.elements[current.groupId];
        if (target) target.name = `Collage ${layout.label}`;
      });
      lastCollage = { groupId: current.groupId, layoutId: layout.id };
      return;
    }
  }
  const parents = buildParentMap(doc);
  const selected = selection.length === 1 ? doc.elements[selection[0]!] : undefined;
  const box = selected && !selected.locked ? worldBox(doc, selected.id, parents) : null;
  const target = box && !box.rotation ? box : collagePageBox(doc);
  const placed = buildCollage(layout, target, {
    ...opts,
    gap: opts.gap ?? defaultCollageGap(target),
  });
  // Replaced: the selected placeholder, or the previous collage while nothing is filled.
  const replace =
    selected &&
    parents.get(selected.id)?.kind === "page" &&
    (isImagePlaceholder(selected) ||
      (current !== null &&
        selected.type === "group" &&
        selected.children.every((id) => isImagePlaceholder(doc.elements[id]))))
      ? selected.id
      : null;
  const group = placed.roots[0]!;
  state.update((draft) => {
    const page = draft.pages[pageIndex];
    const index = replace && page ? page.children.indexOf(replace) : undefined;
    if (replace) removeElements(draft, [replace]);
    for (const element of placed.elements) {
      if (element.id !== group.id) draft.elements[element.id] = element;
    }
    insertElements(draft, pageIndex, [group], index === -1 ? undefined : index);
  });
  state.select([group.id]);
  lastCollage = { groupId: group.id, layoutId: layout.id };
}

/**
 * "Collage" in the Elemente panel (designs): grid layouts of image
 * placeholders; gap, rounding and form apply to the next collage and to the
 * selected one inserted last.
 */
export function CollageInsert() {
  const store = useEditorApi();
  const kind = useEditor((s) => s.doc.kind);
  const width = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).width);
  const height = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).height);
  const selection = useEditor((s) => s.selection);
  const short = Math.min(width, height);
  const [gap, setGap] = useState(() => Math.round(short * 0.02));
  const [radius, setRadius] = useState(0);
  const [maskId, setMaskId] = useState<string | null>(null);
  if (kind === "video") return null;
  const active =
    lastCollage && selection.length === 1 && selection[0] === lastCollage.groupId
      ? lastCollage
      : null;
  /** Applies changed options to the selected collage (one undo step per drag). */
  const apply = (next: CollageOptions, merge: string) => {
    const current = selectedCollage(store);
    const layout = current ? getCollageLayout(current.layoutId) : undefined;
    if (!current || !layout) return;
    store
      .getState()
      .update((draft) => void relayoutCollage(draft, current.groupId, layout, next), { merge });
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-5 gap-1.5">
        {COLLAGE_LAYOUTS.map((layout) => (
          <button
            key={layout.id}
            type="button"
            title={`Collage: ${layout.label}`}
            aria-label={`Collage: ${layout.label}`}
            onClick={() => insertCollage(store, layout, options(gap, radius, maskId))}
            className={cn(
              "flex aspect-square items-center justify-center rounded-lg border bg-zinc-50 text-zinc-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700",
              active?.layoutId === layout.id
                ? "border-brand-500 text-brand-700"
                : "border-zinc-200",
            )}
          >
            <CollagePreview layout={layout} />
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[4.5rem_1fr_2.5rem] items-center gap-x-2 gap-y-1 text-xs text-zinc-600">
        <span>Abstand</span>
        <Slider
          value={gap}
          min={0}
          max={Math.round(short * 0.1)}
          onChange={(value) => {
            setGap(value);
            apply(options(value, radius, maskId), "collage-gap");
          }}
        />
        <span className="text-right tabular-nums">{gap}</span>
        <span>Rundung</span>
        <Slider
          value={radius}
          min={0}
          max={Math.round(short * 0.1)}
          onChange={(value) => {
            setRadius(value);
            apply(options(gap, value, maskId), "collage-radius");
          }}
        />
        <span className="text-right tabular-nums">{radius}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="mr-1 text-xs text-zinc-600">Form</span>
        {[{ id: null, label: "Keine", mask: undefined }, ...CELL_MASKS].map((preset) => (
          <button
            key={preset.id ?? "none"}
            type="button"
            title={preset.label}
            aria-label={`Collage-Form: ${preset.label}`}
            aria-pressed={maskId === preset.id}
            onClick={() => {
              setMaskId(preset.id);
              apply(options(gap, radius, preset.id), "collage-mask");
            }}
            className={cn(
              "flex size-7 items-center justify-center rounded-md border text-zinc-600 hover:bg-zinc-50",
              maskId === preset.id
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-zinc-200",
            )}
          >
            <MaskIcon mask={preset.mask} size={18} />
          </button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500">
        Füllt die Seite (innerhalb der Ränder) oder das ausgewählte Element. Bilder aus den Medien
        auf die Felder ziehen – oder bei ausgewählter Collage anklicken, sie füllen die Felder der
        Reihe nach.
      </p>
    </div>
  );
}
